import styled, { css } from 'styled-components';
import { FlexView } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';

import COLORS from 'constants/colors';

// Flex containers extend `FlexView`, so direction and alignment are its props rather than
// declarations repeated here. Its `gap`/`gutter` props are *padding*, not the CSS `gap`
// these components space children with, so padding stays written out.
//
// The raw styled.div containers are deliberate: Tiles, PanelSplit and Two are CSS grid,
// which FlexView cannot express; Panel, TableWrap, Scrim and Spacer are not flex
// containers at all, and FlexView would hand them flex semantics they do not want.
//
// Typography goes through web-comps' <Text>, per CLAUDE.md: every text node here is a
// styled(Text) whose variant, size, weight and colour are props rather than declarations.
// The variant presets alone could not carry this screen -- they offer 12, 14, 16, 18, 24
// and 28px against the eleven sizes here, from 9.5 to 25px -- but <Text> takes size,
// weight, lineHeight and italic overrides, so the scale survives the move intact. The
// body variants are used rather than the heading ones because h1-h6 pin an Athletics face
// and h3/h5 force uppercase, where this screen sets Untitled Sans throughout.
//
// What stays declared here, and why: text-transform and font-variant-numeric, which no
// variant expresses and which CLAUDE.md does not name; and font-size on the eight leaf
// controls -- Btn, MonoCopy, CloseBtn, PhotoPicker, Field, Input, Textarea,
// NarrativeTextarea -- because a button, label or input cannot be a <Text> and stay
// interactive. Letter-spacing is gone entirely rather than reintroduced in a block, so
// the tile counts and the uppercase labels sit at their variant's tracking now.
//
// Every colour and size here comes from constants/colors and mirrors THEME.FONT.SIZES.
// The screen used to carry its own palette of literal hex, which is the main reason it did
// not look like the rest of the admin. THEME itself is not imported: it pulls in global CSS
// that app.js already owns.

const RULE = COLORS.WHITE_4;
const RULE_SOFT = COLORS.WHITE_30;
const INK = COLORS.COLA;
const INK_2 = COLORS.COLA_LIGHT;
const INK_3 = COLORS.COLA_LIGHTER;
const BRAND = COLORS.SECONDARY;
const BRAND_GRADIENT = `linear-gradient(56.99deg, ${COLORS.SECONDARY} 0%, #1F48D7 100%)`;

// A white-to-CLOUD wash over the first 180px rather than a flat grey field: it gives the
// title and the tiles air, and lets the records panel below read as sitting *on* the page.
export const Root = styled(FlexView)`
  padding: 20px 22px 32px;
  gap: 16px;
  background: ${COLORS.CLOUD}
    linear-gradient(180deg, ${COLORS.WHITE} 0%, ${COLORS.CLOUD} 180px);
  background-repeat: no-repeat;
  min-height: 100%;
  color: ${INK};
`;

export const Row = styled(FlexView).attrs({
  flexDirection: 'row',
  alignItems: 'center',
})`
  gap: 12px;
  flex-wrap: wrap;
`;

// --- page header ------------------------------------------------------------

export const PageHead = styled(FlexView).attrs({
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
})`
  gap: 20px;
`;
export const PageTitle = styled(Text).attrs({
  variant: 'body1Medium',
  size: 20,
  weight: 700,
  lineHeight: 26,
  color: INK,
})``;
export const PageSub = styled(Text).attrs({
  variant: 'body3',
  size: 12,
  lineHeight: 16,
  color: INK_2,
})`
  margin-top: 2px;
`;
export const HeadActions = styled(FlexView).attrs({
  flexDirection: 'row',
  alignItems: 'center',
})`
  gap: 8px;
  flex: none;
`;

// --- status tiles: the page's filter, and its only view of the backlog ------

// One tone system, three surfaces. A status has a pale `tint` for chips and for the selected
// tile's wash, a dark `ink` that is legible on that tint, and a saturated `edge` for the
// 3px rules and hover accents — a hairline has to carry the colour on its own, which a pale
// tint cannot do. Keeping these together is what lets the tile, the chip and the row accent
// read as the same signal rather than three coincidences.

// Purple is the one tone here that is not a stage in the queue: it marks the two things on
// this screen that are neither progress nor a problem.
const PURPLE = { tint: COLORS.PURPLE_1, ink: COLORS.PURPLE_6, edge: COLORS.PURPLE_4 };

const TONES = {
  review: { tint: COLORS.YELLOW_1, ink: COLORS.YELLOW_6, edge: COLORS.YELLOW_3 },
  ready: { tint: COLORS.BLUE_1, ink: COLORS.BLUE_6, edge: COLORS.BLUE_4 },
  published: { tint: COLORS.GREEN_1, ink: COLORS.GREEN_6, edge: COLORS.GREEN_4 },
  parked: { tint: COLORS.ORANGE_1, ink: COLORS.ORANGE_6, edge: COLORS.ORANGE_4 },
  logged: { tint: COLORS.GREY_1, ink: COLORS.BLACK_65, edge: COLORS.GREY_3 },
  removed: { tint: COLORS.WHITE_4, ink: COLORS.GRAY, edge: COLORS.WHITE_5 },
  // An impact story is a real thing that is simply not an achievement, so it gets a colour
  // of its own. The rows judged not to belong keep `logged`'s grey: grey is the right
  // signal for a row nobody has to act on.
  story: PURPLE,
  // Named for what it paints — the record's category label. It was called `tier`, which is
  // now a real field on the row and a different thing entirely.
  category: PURPLE,
  granted: { tint: COLORS.GREEN_1, ink: COLORS.GREEN_6, edge: COLORS.GREEN_4 },
  denied: { tint: COLORS.RED_1, ink: COLORS.RED_6, edge: COLORS.RED_4 },
};

const toneOf = key => TONES[key] || null;

export const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
`;
// Selected state is the tone's own tint, not a blue ring: the ring said "chosen" while the
// colour said "parked", and the eye had to reconcile two unrelated signals. Now picking the
// Parked queue washes the tile in the same orange its chips carry.
export const Tile = styled.button`
  position: relative;
  text-align: left;
  font-family: inherit;
  border-radius: 4px;
  padding: 11px 13px 12px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  cursor: pointer;
  transition: background 0.14s ease, border-color 0.14s ease, transform 0.14s ease;
  animation: hof-rise 0.34s both cubic-bezier(0.2, 0.7, 0.3, 1);
  ${p => {
    const t = toneOf(p.tone) || TONES.removed;
    return css`
      background: ${p.active ? t.tint : COLORS.WHITE};
      border: 1px solid ${p.active ? t.edge : RULE};
      border-top: 3px solid ${t.edge};
      box-shadow: ${p.active ? 'none' : `0 1px 2px ${COLORS.BLACK_T_04}`};
      color: ${p.active ? t.ink : INK};
      animation-delay: ${(p.index || 0) * 45}ms;
      &:hover {
        background: ${p.active ? t.tint : COLORS.WHITE_2};
        border-color: ${t.edge};
      }
    `;
  }}
  &:focus-visible {
    outline: 2px solid ${BRAND};
    outline-offset: 2px;
  }
  @keyframes hof-rise {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;
// The count is the reason the tile exists, so it gets the optical treatment: tabular figures
// so six tiles line up, and negative tracking because large digits set loose look accidental.
export const TileCount = styled(Text).attrs(p => ({
  variant: 'body1Medium',
  size: 25,
  weight: 700,
  lineHeight: 26,
  color: p.unknown ? INK_3 : 'inherit',
}))`
  font-variant-numeric: tabular-nums;
`;
export const TileLabel = styled(Text).attrs({
  variant: 'body3',
  size: 9.5,
  weight: 700,
  lineHeight: 13,
  color: INK_2,
})`
  text-transform: uppercase;
`;
export const TileHint = styled(Text).attrs({
  variant: 'body3',
  size: 10,
  lineHeight: 13,
  color: INK_3,
})``;

// --- panels and tables ------------------------------------------------------

export const Panel = styled.div`
  background: ${COLORS.WHITE};
  border: 1px solid ${RULE};
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 2px ${COLORS.BLACK_T_04};
`;
export const PanelBar = styled(FlexView).attrs({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
})`
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid ${RULE};
`;
export const PanelTitle = styled(Text).attrs({
  variant: 'body1Medium',
  size: 13,
  weight: 700,
  lineHeight: 17,
  color: INK,
})``;
export const TableWrap = styled.div`
  /* Keep the previous list on screen while refetching so the scroll position
     (and the row you were about to act on) survives every action. */
  opacity: ${p => (p.busy ? 0.45 : 1)};
  pointer-events: ${p => (p.busy ? 'none' : 'auto')};
  transition: opacity 0.15s ease;
  /* A real scroll box, following comps/table. It has to be one for the sticky header to
     have anything to stick to: overflow-x alone makes this a scroll container on both
     axes, and with no height the header would pin to a viewport that never moves. At the
     default 10 rows the content is shorter than this, so no scrollbar appears. */
  overflow: auto;
  max-height: calc(100vh - 330px);
  min-height: 120px;
`;
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  /* Sticky, because a 50-row queue scrolls past the header and then every column is a
     guess. z-index keeps it over the rows, not the review panel. */
  th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: ${COLORS.CLOUD};
    text-align: left;
    padding: 8px 12px;
    border-bottom: 1px solid ${RULE};
    white-space: nowrap;
  }
  td {
    padding: 10px 12px;
    border-bottom: 1px solid ${RULE_SOFT};
    vertical-align: middle;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
`;
// Clicking anywhere on the row opens the review panel — the row is the affordance, not just
// the button at the end of it. Hover puts a 2px bar in the row's own status tone against the
// leading edge, which says "this responds" and repeats the signal already in its chip.
export const QueueRow = styled.tr`
  cursor: ${p => (p.clickable ? 'pointer' : 'default')};
  ${p => {
    if (!p.clickable) return '';
    const t = toneOf(p.tone) || TONES.removed;
    return css`
      &:hover td {
        background: ${COLORS.WHITE_2};
      }
      &:hover td:first-child {
        box-shadow: inset 2px 0 0 ${t.edge};
      }
    `;
  }}
`;
export const NumCell = styled.td`
  font-variant-numeric: tabular-nums;
`;

export const Who = styled(FlexView).attrs({
  flexDirection: 'row',
  alignItems: 'center',
})`
  gap: 9px;
`;
// Initials rather than a photo: the queue is scanned, and a row of 30px faces reads
// as decoration. The photo belongs in the review panel, where it is being approved.
export const Avatar = styled(FlexView).attrs({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
})`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  flex: none;
  background: ${COLORS.WHITE_4};
  color: ${INK_2};
  position: relative;
  overflow: hidden;
`;
// Sits on top of the initials rather than replacing them, so the circle reads correctly
// while the image is still loading and falls back to them if it never arrives -- an empty
// disc beside a name looks like a rendering fault, initials do not.
export const AvatarImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
// `unnamed` recedes: a tutor row has no name until the card is assembled, and setting that
// placeholder in the same bold ink as a real name made 58 rows all shout a non-name.
export const WhoName = styled(Text).attrs(p => ({
  variant: p.unnamed ? 'body3' : 'body1Medium',
  size: 12,
  weight: p.unnamed ? 400 : 600,
  lineHeight: 16,
  italic: !!p.unnamed,
  color: p.unnamed ? INK_3 : INK,
}))``;
export const WhoMeta = styled(Text).attrs({
  variant: 'body3',
  size: 11,
  lineHeight: 15,
  color: INK_2,
})``;
export const Primary = styled(Text).attrs({
  variant: 'body1Medium',
  size: 12,
  weight: 600,
  lineHeight: 16,
  color: INK,
})``;
export const Secondary = styled(Text).attrs({
  variant: 'body3',
  size: 11,
  lineHeight: 15,
  color: INK_2,
})``;

// A parked record's reason is the one thing the admin has to read, so it gets the full
// width of the table under its row instead of a narrow column that wraps to four lines.
export const ReasonRow = styled.tr`
  td {
    background: ${COLORS.ORANGE_1}4D;
    padding: 8px 12px 9px 53px;
    border-bottom: 1px solid ${RULE_SOFT};
  }
`;
export const ReasonText = styled(FlexView).attrs({
  flexDirection: 'row',
  alignItems: 'flex-start',
})`
  gap: 9px;
  color: ${COLORS.ORANGE_6};
`;

export const Pager = styled(FlexView).attrs({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
})`
  gap: 12px;
  padding: 10px 14px;
  border-top: 1px solid ${RULE};
  color: ${INK_2};
  flex-wrap: wrap;
`;

// Dropdown pins its chevron to this box edge and sizes only the field from width, so the
// box has to be the field width or the chevron drifts away from it.
export const RowsPicker = styled(FlexView)`
  width: 76px;
  flex: none;
`;

// --- buttons ----------------------------------------------------------------

// Weight tracks consequence, not prominence: the brand gradient marks the one action a
// zone exists for, publish is grave rather than bright, destructive is the only red, and
// everything reversible stays quiet.
const BTN_VARIANTS = {
  primary: { bg: BRAND_GRADIENT, fg: COLORS.WHITE, border: 'transparent' },
  publish: { bg: COLORS.ABBEY, fg: COLORS.WHITE, border: COLORS.ABBEY },
  danger: {
    bg: COLORS.WHITE,
    fg: COLORS.RED_6,
    border: COLORS.RED_2,
    hoverBg: COLORS.RED_1,
  },
  neutral: {
    bg: COLORS.WHITE,
    fg: INK,
    border: COLORS.WHITE_5,
    hoverBg: COLORS.CLOUD,
  },
};
export const Btn = styled.button`
  font-family: inherit;
  font-size: ${p => (p.small ? 11 : 12)}px;
  font-weight: 600;
  height: ${p => (p.small ? 26 : 32)}px;
  padding: 0 ${p => (p.small ? 9 : 14)}px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  transition: filter 0.12s ease, background 0.12s ease;
  ${p => {
    const v = BTN_VARIANTS[p.variant] || BTN_VARIANTS.neutral;
    return css`
      background: ${v.bg};
      color: ${v.fg};
      border: 1px solid ${v.border};
      &:hover:not(:disabled) {
        background: ${v.hoverBg || v.bg};
        filter: ${v.hoverBg ? 'none' : 'brightness(0.94)'};
      }
    `;
  }}
  &:focus-visible {
    outline: 2px solid ${BRAND};
    outline-offset: 2px;
  }
`;

// --- chips ------------------------------------------------------------------

// Semantic only — none of these is the accent, which stays the brand blue. Tones come from
// the shared TONES map, so a chip can never drift from the tile that filters to it.
// The tone-driven colour stays in the block: the tint/ink/edge system is this screen's
// design, and Text takes a single colour rather than a pair.
export const Pill = styled(Text).attrs({
  variant: 'body3',
  size: 10.5,
  weight: 700,
  lineHeight: 14,
  display: 'inline-flex',
})`
  align-items: center;
  padding: 3px 8px;
  border-radius: 10px;
  white-space: nowrap;
  ${p => {
    const t = toneOf(p.tone);
    return t
      ? css`
          background: ${t.tint};
          color: ${t.ink};
        `
      : css`
          background: transparent;
          color: ${INK_3};
          border: 1px solid ${COLORS.WHITE_5};
        `;
  }}
`;

// The status cell's second chip goes under the first, not beside it. "Not for the Hall of
// Fame" is a long chip that must not wrap, and side by side the pair widens the Status
// column past the achievement it is there to be read against.
export const TierPill = styled(Pill)`
  display: flex;
  width: fit-content;
  margin-top: 4px;
`;

export const Muted = styled(Text).attrs({
  variant: 'body3',
  size: 11,
  lineHeight: 15,
  color: INK_2,
})``;
export const Msg = styled(Text).attrs(p => ({
  variant: 'body3',
  size: 11.5,
  lineHeight: 16,
  color: p.error ? COLORS.RED_6 : COLORS.GREEN_6,
}))``;

// Slugs and ids are machine strings: truncate them, and let a click copy the full value
// rather than forcing an eight-line cell. comps/copy-button is used in the review panel,
// where its bordered box fits; inside a table cell that box is too heavy.
export const MonoCopy = styled.button`
  display: block;
  max-width: 210px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: monospace;
  font-size: 10px;
  text-align: left;
  color: ${INK_3};
  background: none;
  border: none;
  padding: 0;
  margin-top: 2px;
  cursor: pointer;
  &:hover {
    color: ${BRAND};
    text-decoration: underline;
  }
`;

// --- review panel -----------------------------------------------------------

export const Scrim = styled.div`
  position: fixed;
  inset: 0;
  background: ${COLORS.BLACK_T_20};
  z-index: 49;
`;
// Wider than the shared comps/drawer's 352px on purpose: the card being approved and the
// fields that change it have to be visible at the same time. Adopting the shared drawer's
// header, shadow and slide keeps it looking like one.
export const Panel2 = styled(FlexView)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 620px;
  max-width: 100%;
  background: ${COLORS.WHITE};
  box-shadow: -2px 0 14px ${COLORS.BLACK_T_20};
  z-index: 50;
  transform: translateX(0);
  animation: hof-slide-in 0.22s ease-out;
  @keyframes hof-slide-in {
    from {
      transform: translateX(100%);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
export const PanelHead = styled(FlexView).attrs({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
})`
  height: 52px;
  flex: none;
  background: ${COLORS.COLA};
  color: ${COLORS.WHITE};
  gap: 12px;
  padding: 0 16px;
`;
export const PanelHeadTitle = styled(Text).attrs({
  variant: 'body1Medium',
  size: 14,
  weight: 700,
  lineHeight: 18,
  color: INK,
})`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
export const CloseBtn = styled.button`
  background: none;
  border: none;
  color: ${COLORS.WHITE};
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
  opacity: 0.85;
  &:hover {
    opacity: 1;
  }
`;
export const PanelSplit = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 238px 1fr;
  min-height: 0;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;
export const PreviewCol = styled(FlexView)`
  border-right: 1px solid ${RULE};
  background: ${COLORS.WHITE_2};
  padding: 14px;
  gap: 10px;
  overflow-y: auto;
`;
export const ColLabel = styled(Text).attrs({
  variant: 'body3',
  size: 9.5,
  weight: 700,
  lineHeight: 13,
  color: INK_2,
})`
  text-transform: uppercase;
`;
export const PreviewCard = styled(FlexView).attrs({
  alignItems: 'center',
  align: 'center',
})`
  border: 1px solid ${RULE};
  border-radius: 6px;
  background: ${COLORS.WHITE};
  padding: 14px 12px;
  gap: 7px;
`;
// Fixed square so the panel does not reflow between a portrait and a landscape photo;
// object-fit crops instead of distorting the child's face.
export const PreviewPhoto = styled.img`
  width: 68px;
  height: 68px;
  border-radius: 50%;
  object-fit: cover;
  background: ${COLORS.WHITE_4};
  display: block;
`;
// The preview crops to a 68px circle, so a moderator cannot see the frame that actually
// gets published. This opens it whole. A leaf button rather than a click handler on the
// image, so it is reachable by keyboard and reads as a control.
export const PhotoZoomBtn = styled.button`
  padding: 0;
  border: 0;
  background: none;
  display: block;
  border-radius: 50%;
  cursor: zoom-in;
`;
// Above the drawer (50) and its scrim (49), or the expanded photo opens behind the panel
// that launched it.
export const PhotoZoomScrim = styled(FlexView).attrs({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
})`
  position: fixed;
  inset: 0;
  z-index: 51;
  padding: 40px;
  background: ${COLORS.BLACK_65};
  cursor: zoom-out;
`;
// `contain`, not `cover`: the point of expanding is to see the whole frame uncropped.
export const ZoomedPhoto = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
  background: ${COLORS.WHITE};
`;
export const PreviewName = styled(Text).attrs({
  variant: 'body1Medium',
  size: 14,
  weight: 700,
  lineHeight: 18,
  color: INK,
})``;
export const PreviewScore = styled(Text).attrs({
  variant: 'body3',
  size: 11,
  lineHeight: 15,
  color: INK_2,
})``;
export const PreviewNarrative = styled(Text).attrs({
  variant: 'body3',
  size: 10.5,
  lineHeight: 15,
  color: INK_2,
})`
  line-height: 1.5;
  text-align: left;
  align-self: stretch;
`;
export const FieldsCol = styled(FlexView)`
  padding: 14px 16px;
  gap: 10px;
  overflow-y: auto;
`;
export const PanelFoot = styled(FlexView).attrs({
  flexDirection: 'row',
  alignItems: 'center',
})`
  flex: none;
  border-top: 1px solid ${RULE};
  padding: 11px 16px;
  gap: 8px;
  background: ${COLORS.WHITE};
`;
export const Spacer = styled.div`
  flex: 1;
`;

// A styled <label> wrapping a hidden file input: the native control cannot be restyled
// and looks foreign next to the panel's buttons.
export const PhotoPicker = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  padding: 0 9px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: ${COLORS.WHITE};
  color: ${INK};
  border: 1px solid ${COLORS.WHITE_5};
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  &:hover {
    background: ${p => (p.disabled ? COLORS.WHITE : COLORS.CLOUD)};
  }
  input {
    display: none;
  }
`;

// --- form controls ----------------------------------------------------------

export const Field = styled.label`
  display: block;
  font-size: 10.5px;
  font-weight: 700;
  color: ${INK};
`;
export const Hint = styled(Text).attrs({
  variant: 'body3',
  size: 10,
  lineHeight: 14,
  color: INK_3,
})``;
// Somebody's own prose, and the one kind of thing in this column nobody may edit. Tinted
// and rule-marked so it does not read as another empty textarea, and `pre-wrap` because
// the line breaks they typed are part of what they wrote.
export const NoteBlock = styled(Text).attrs({
  variant: 'body3',
  size: 12,
  lineHeight: 17,
  color: INK,
})`
  line-height: 1.5;
  background: ${COLORS.WHITE_2};
  border-left: 3px solid ${COLORS.GREY_3};
  border-radius: 3px;
  padding: 8px 10px;
  white-space: pre-wrap;
`;
// --- triage ------------------------------------------------------------------

// One of the three destinations, as a leaf button rather than a native radio: the choice
// carries a line of explanation under it, which a radio's label cannot hold legibly. The
// selected state paints in the tone of the queue the record is about to land in, so the
// colour a moderator picks is the colour they will next see the row under.
export const TierOption = styled.button`
  display: flex;
  gap: 9px;
  align-items: flex-start;
  width: 100%;
  text-align: left;
  padding: 9px 11px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;
  ${p => {
    const t = toneOf(p.tone);
    return p.selected && t
      ? css`
          background: ${t.tint};
          border: 1px solid ${t.edge};
        `
      : css`
          background: ${COLORS.WHITE};
          border: 1px solid ${COLORS.WHITE_5};
          &:hover {
            background: ${COLORS.CLOUD};
          }
        `;
  }}
  &:focus-visible {
    outline: 2px solid ${BRAND};
    outline-offset: 2px;
  }
`;

// A scorecard is read, not glanced at: wide rectangle, not the card's round portrait crop.
// Wrapped in a link because the moderator's actual move is to open it full size.
export const EvidenceThumb = styled.img`
  display: block;
  width: 100%;
  max-height: 180px;
  object-fit: contain;
  border: 1px solid ${COLORS.WHITE_5};
  border-radius: 4px;
  background: ${COLORS.WHITE_2};
`;
export const EvidenceLink = styled.a`
  display: block;
  text-decoration: none;
  &:focus-visible {
    outline: 2px solid ${BRAND};
    outline-offset: 2px;
  }
`;

// Text, where `EvidenceLink` wraps a thumbnail -- but the same reset and focus ring.
export const PublicPageLink = styled(EvidenceLink)`
  align-self: flex-start;
  margin-top: 6px;
  color: ${BRAND};
  font-size: 12px;
  &:hover {
    text-decoration: underline;
  }
`;

export const TierDot = styled.span`
  width: 13px;
  height: 13px;
  border-radius: 50%;
  flex: none;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${p => {
    const t = toneOf(p.tone);
    return p.selected && t
      ? css`
          border: 1.5px solid ${t.ink};
          background: ${t.ink};
        `
      : css`
          border: 1.5px solid ${COLORS.WHITE_5};
          background: ${COLORS.WHITE};
        `;
  }}
  span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${p => (p.selected ? COLORS.WHITE : 'transparent')};
  }
`;
export const TierLabel = styled(Text).attrs({
  variant: 'body3',
  size: 12,
  weight: 700,
  lineHeight: 16,
  color: INK,
})``;
// Hangs off the bottom of the chosen option rather than sitting after all three, so it
// reads as belonging to that choice and not to the section.
export const TierFields = styled(FlexView)`
  border: 1px solid ${p => (toneOf(p.tone) || {}).edge || COLORS.WHITE_5};
  border-top: none;
  border-radius: 0 0 4px 4px;
  background: ${COLORS.WHITE_2};
  margin-top: -8px;
  padding: 12px 11px 11px;
  gap: 10px;
`;

export const FieldRow = styled(FlexView)`
  gap: 3px;
`;
export const Input = styled.input`
  font-family: inherit;
  width: 100%;
  height: 30px;
  padding: 0 9px;
  font-size: 12px;
  color: ${INK};
  border: 1px solid ${COLORS.WHITE_5};
  border-radius: 4px;
  background: ${COLORS.WHITE};
  &:focus {
    outline: none;
    border-color: ${BRAND};
    box-shadow: 0 0 0 2px ${COLORS.SKY_XXLIGHT};
  }
`;
const Textarea = styled.textarea`
  font-family: monospace;
  width: 100%;
  min-height: 120px;
  font-size: 12px;
  padding: 8px;
  color: ${INK};
  border: 1px solid ${COLORS.WHITE_5};
  border-radius: 4px;
  background: ${COLORS.WHITE};
`;
// Tall enough for the agent's usual three-line paragraph, so the common case is not read
// through a scrollbar.
export const NarrativeTextarea = styled(Textarea)`
  font-family: inherit;
  min-height: 82px;
  font-size: 11.5px;
  line-height: 1.5;
`;
export const Two = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

// --- modal ------------------------------------------------------------------

// --- exam picker ------------------------------------------------------------

export const EmptyState = styled(FlexView).attrs({
  alignItems: 'center',
  align: 'center',
})`
  padding: 52px 20px 56px;
  color: ${INK_2};
  gap: 6px;
`;
// A cleared queue is good news, so the empty state gets a mark rather than a bare sentence.
export const EmptyMark = styled(FlexView).attrs({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
})`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  margin-bottom: 4px;
  /* FlexView applies flex: 1, which would stretch this fixed disc — same reason Avatar
     pins it. */
  flex: none;
  background: ${COLORS.CLOUD};
  border: 1px solid ${RULE};
  color: ${INK_3};
`;
export const EmptyTitle = styled(Text).attrs({
  variant: 'body1Medium',
  size: 13,
  weight: 700,
  lineHeight: 17,
  color: INK,
})``;

// The row-end action cluster in a queue row. Tighter than Row's 12px and pinned right, so
// Review and Publish sit against the table edge rather than floating mid-cell.
export const RowActions = styled(Row)`
  gap: 6px;
  justify-content: flex-end;
`;

// The review panel's header cluster: the unsaved-changes chip beside the close button.
export const PanelHeadActions = styled(FlexView).attrs({
  flexDirection: 'row',
  alignItems: 'center',
})`
  gap: 10px;
  flex: none;
`;

// Text nodes that sit inside a container which cannot itself be a Text: a table header
// cell, the pager strip, the parked-reason row, the avatar disc and the empty-state mark.
export const ThLabel = styled(Text).attrs({
  variant: 'body3',
  size: 10,
  weight: 700,
  lineHeight: 13,
  color: INK_2,
})`
  text-transform: uppercase;
  white-space: nowrap;
`;
export const PagerText = styled(Text).attrs({
  variant: 'body3',
  size: 11,
  lineHeight: 15,
  color: INK_2,
})``;
export const ReasonBody = styled(Text).attrs({
  variant: 'body3',
  size: 11.5,
  lineHeight: 16,
  color: COLORS.ORANGE_6,
})``;
export const AvatarInitials = styled(Text).attrs({
  variant: 'body1Medium',
  size: 11,
  weight: 700,
  lineHeight: 15,
  color: INK_2,
})``;
export const EmptyGlyph = styled(Text).attrs({
  variant: 'body3',
  size: 15,
  lineHeight: 18,
  color: INK_3,
})``;
export const Body = styled(Text).attrs({
  variant: 'body3',
  size: 12,
  lineHeight: 17,
  color: INK,
})``;
